import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { fetchTaskDialAttempts } from '@/api/callHistoryApi'
import { getApiMessage, isApiSuccess } from '@/mixins/Extras/ApiResponse'
import { formatPhoneNumber, displayOrBlank, EMPTY_DISPLAY } from '@/utils/format'

// listTaskDialAttempts pages server-side (limit/next_token) rather than
// returning everything in one shot the way listCallHistory does. A campaign's
// full dial log (every attempt, not just connected ones) is still bounded by
// leads x max_dial_attempts, so this follows next_token to completion on
// load rather than exposing a "load more" button -- the summary strip and
// client-side pagination below both need the whole campaign's numbers to be
// accurate, not just whatever page happened to load first.
const PAGE_FETCH_LIMIT = 100

// Runaway guard only -- stops an unexpected endless next_token from hanging
// the tab forever. Not expected to bite for any real campaign today.
const MAX_AUTO_PAGES = 200

function normalizeOutcome(value) {
  const outcome = String(value ?? '').trim().toUpperCase()
  return outcome === 'COMPLETED' ? 'COMPLETED' : 'NOT_ANSWERED'
}

function normalizeDialAttemptRow(raw, idx) {
  const outcome = normalizeOutcome(raw?.outcome)
  const callerNumber = raw?.caller_number
    ? formatPhoneNumber(raw.caller_number)
    : EMPTY_DISPLAY

  return {
    // call_uuid first, not history_id -- CallIntelligenceDrawer/
    // useCallIntelligence key feedback lookups off `call.id`, and feedback
    // is stored against the call's own uuid (the same id
    // useCallHistoryData's normalizeCallRow uses), not this dial-attempt
    // log row's own id. history_id is still unique per row, so it's a safe
    // fallback (and the v-for :key) if call_uuid were ever missing.
    id: raw?.call_uuid ?? raw?.history_id ?? `dial-attempt-${idx}`,
    history_id: raw?.history_id ?? null,
    call_uuid: raw?.call_uuid ?? null,
    request_id: raw?.request_id ?? null,
    task_id: raw?.task_id ?? null,
    created_on: raw?.created_on ?? null,
    outcome,
    comments: String(raw?.comments ?? '').trim(),
    duration_seconds: raw?.duration_seconds ?? null,
    recording_s3_key: raw?.recording_s3_key ?? null,
    recording_s3_url: raw?.recording_s3_url ?? null,
    agent_id: raw?.agent_id ?? null,
    agent_name: raw?.agent_name ?? null,
    caller_name: displayOrBlank(raw?.caller_name, EMPTY_DISPLAY),
    caller_number: callerNumber,
    // Shaped so this row can be handed straight to CallIntelligenceDrawer /
    // useCallIntelligence for COMPLETED rows -- it reads from_number,
    // start_time, duration_seconds and recording_s3_key/url. There is
    // deliberately no `transcript` field: listTaskDialAttempts doesn't
    // return one, so the drawer's transcript panel shows its normal "no
    // transcript" empty state for these rows.
    from_number: callerNumber,
    start_time: raw?.created_on ?? null,
  }
}

/**
 * task_id-scoped log of every dial attempt a campaign made -- connected or
 * not. Sibling to useCallHistoryData, but backed by listTaskDialAttempts
 * instead of listCallHistory, and paginates via next_token instead of
 * returning everything in one response.
 */
export function useTaskDialAttemptsData(options = {}) {
  const { taskId } = options
  const store = useStore()

  const attempts = ref([])
  const loading = ref(false)
  const page = ref(1)
  const itemsPerPage = ref(25)

  const isSessionReady = computed(() => store.getters.isSessionReady)

  const totalAttempts = computed(() => attempts.value.length)
  const connectedCount = computed(
    () => attempts.value.filter((row) => row.outcome === 'COMPLETED').length,
  )
  const notAnsweredCount = computed(
    () => totalAttempts.value - connectedCount.value,
  )

  const pageCount = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return 1
    return Math.max(1, Math.ceil(attempts.value.length / perPage))
  })

  const paginatedAttempts = computed(() => {
    const perPage = itemsPerPage.value
    if (perPage <= 0) return attempts.value
    const start = (page.value - 1) * perPage
    return attempts.value.slice(start, start + perPage)
  })

  const rangeLabel = computed(() => {
    const total = attempts.value.length
    if (!total) return '0 of 0'
    const perPage = itemsPerPage.value
    if (perPage <= 0) return `1-${total} of ${total}`
    const start = (page.value - 1) * perPage + 1
    const end = Math.min(page.value * perPage, total)
    return `${start}-${end} of ${total}`
  })

  // current_user_id is injected server-side by the AppSync resolver from
  // $context.identity.username (see AdminListTaskDialAttemptsQueryResolver)
  // -- NOT a client-supplied field. listTaskDialAttemptsInput's schema
  // doesn't declare it, same convention listCallHistoryInput already uses;
  // sending it anyway trips GraphQL's strict input-field validation
  // ("field is not defined for input object type"). Confirmed live
  // 2026-08-26.
  function buildBaseInput() {
    const tid = String(taskId?.value ?? '').trim()
    return {
      task_id: tid,
    }
  }

  async function loadDialAttempts() {
    if (!isSessionReady.value) return { ok: false }

    const baseInput = buildBaseInput()
    if (!baseInput.task_id) {
      attempts.value = []
      return { ok: false }
    }

    loading.value = true
    try {
      const rows = []
      let nextToken = ''
      let pagesFetched = 0

      do {
        const response = await fetchTaskDialAttempts({
          ...baseInput,
          limit: PAGE_FETCH_LIMIT,
          ...(nextToken ? { next_token: nextToken } : {}),
        })

        if (!isApiSuccess(response)) {
          if (!rows.length) {
            attempts.value = []
            return response
          }
          // Already have some pages -- show what loaded rather than
          // throwing it all away over a later page failing.
          break
        }

        const data = response?.data ?? {}
        const items = Array.isArray(data.items) ? data.items : []
        rows.push(...items)
        nextToken = String(data.next_token ?? '').trim()
        pagesFetched += 1
      } while (nextToken && pagesFetched < MAX_AUTO_PAGES)

      attempts.value = rows
        .map(normalizeDialAttemptRow)
        .sort((a, b) => (Number(b.created_on) || 0) - (Number(a.created_on) || 0))
      page.value = 1
      return { ok: true }
    } catch (err) {
      attempts.value = []
      throw err
    } finally {
      loading.value = false
    }
  }

  function setPage(next) {
    page.value = Math.min(Math.max(1, next), pageCount.value)
  }

  function setItemsPerPage(value) {
    itemsPerPage.value = value
    page.value = 1
  }

  watch(
    isSessionReady,
    (ready) => {
      if (ready) loadDialAttempts()
    },
    { immediate: true },
  )

  // Mirrors useCallHistoryData's own taskId watcher -- a no-op for a fixed
  // ref (CampaignDialAttemptsPanel.vue's campaignId never changes after
  // mount), only relevant if a future caller passes a reactive one.
  if (taskId) {
    watch(taskId, () => {
      if (isSessionReady.value) loadDialAttempts()
    })
  }

  return {
    attempts,
    loading,
    page,
    itemsPerPage,
    pageCount,
    isSessionReady,
    paginatedAttempts,
    totalAttempts,
    connectedCount,
    notAnsweredCount,
    rangeLabel,
    loadDialAttempts,
    setPage,
    setItemsPerPage,
  }
}

export function getDialAttemptsErrorMessage(payload) {
  return getApiMessage(payload, 'Failed to load dial attempts')
}
